// src/components/TryInvoiceForm.jsx
import React, { useEffect, useState } from 'react';
import postmanIcon from '../assets/Postman.png'

export default function TryInvoiceForm() {
  //defines the endpoints here
  const endpoints = {
    invoice: {
      method: 'POST',
      path: '/api/v1/invoice/generate',
      description: 'Invoice PDF Generator',
      payload: {
        recipient: {
          name: 'George Hadjisavva',
          email: 'george@example.com',
        },
        sender: {
          name: 'Acme Inc.',
          address: '123 Startup Blvd',
          email: 'billing@acme.com',
          vat_id: 'CY12345678',
        },
        items: [{ description: 'Freelance Development', quantity: 1, unit_price: 1000 }],
        currency: 'USD',
        tax_rate: 0.19,
        due_date: '2025-06-01',
      },
    },
    pdfMerge: {
      method: 'POST',
      path: '/api/v1/pdf/merge',
      description: 'Merges multiple PDFs into single pdf',
      payload: {
        files: [
          "https://morth.nic.in/sites/default/files/dd12-13_0.pdf",
          "https://morth.nic.in/sites/default/files/dd12-13_0.pdf"
        ]
      },
    },
    urlParser: {
      method: 'POST',
      path: '/api/v1/url/parse',
      description: 'URL Parser - Parse articles to json ',
      payload: {
        url: 'https://edition.cnn.com/2025/06/21/politics/iran-trump-conflict-decision',
      },
    },
  };

  // 2. State hooks to manage selected endpoint key, JSON input text, and response output:
  const [selectedEndpoint, setSelectedEndpoint] = useState('invoice');
  const [jsonInput, setJsonInput] = useState('');       // holds the stringified payload
  const [responseOutput, setResponseOutput] = useState('// Response will appear here'); // holds raw response text
  const [isRequestPending, setIsRequestPending] = useState(false)
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobResult, setJobResult] = useState(null);
  const [pollingError, setPollingError] = useState(null);

  // 3. Convenience: compute the display label for the current endpoint
  const getEndpointLabel = (key) => {
    const ep = endpoints[key];
    return (
      <>
        <span className="font-mono text-green-400">{ep.method}</span>
        <span className="font-mono">{ep.path}</span>
        <span className="hidden text-gray-300 sm:inline">– {ep.description}</span>
      </>
    );
  };

  //trigger when jobId changes
  useEffect(() => {
    if (jobId) {
      pollJobStatus(jobId);
    }
  }, [jobId]);

  // 4. On component mount (and whenever selectedEndpoint changes), initialize JSON input:
  useEffect(() => {
    const ep = endpoints[selectedEndpoint];
    // Prettify the JSON payload with 2-space indentation:
    setJsonInput(JSON.stringify(ep.payload, null, 2));
    // Also reset response output placeholder:
    setResponseOutput('// Response will appear here');
  }, [selectedEndpoint]);

  // method to retrieve pool
  const pollJobStatus = async (jobId) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    const maxAttempts = 10;
    const interval = 2000;
    const apiKey = process.env.REACT_APP_API_TOKEN || '';

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const res = await fetch(`${baseUrl}/api/v1/jobs/${jobId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': apiKey,
          },
        });

        const { data } = await res.json();

        if (data.status === 'failed') {
          setJobStatus('failed');
          setPollingError(data.last_error || 'Unknown error');
          return;
        }

        setJobStatus(data.status);
        setJobResult(data)
        await new Promise((r) => setTimeout(r, interval));
      } catch (err) {
        setPollingError('Network or server error.');
        return;
      }
    }

    setPollingError('Timed out waiting for job to complete.');
  };

  // 5. Handler for “Send Request” button:
  const handleSendRequest = async () => {
    setIsRequestPending(true)

    try {
      const payload = JSON.parse(jsonInput);
      const ep = endpoints[selectedEndpoint];

      // Read base URL from an environment variable:
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';

      const res = await fetch(baseUrl + ep.path, {
        method: ep.method,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_TOKEN,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      // Display the entire JSON response nicely:
      console.log(data)
      setJobId(data.data.job_id);
      setIsRequestPending(false)
    } catch (err) {
      setIsRequestPending(false)
      setResponseOutput(`// Error: ${err.message}`);
    }
  };

  return (
    <section id="try-it" className="py-24 px-6 text-white bg-[#0f172a]">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-12 text-center">
        <h2 className="mb-4 text-4xl font-bold">Try the API</h2>
        <p className="text-lg text-gray-300">Select an endpoint and send a live JSON request.</p>
      </div>

      {/* Endpoint Selector */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex flex-col items-center justify-center gap-4 text-sm sm:flex-row">
          <label htmlFor="apiSelect" className="text-gray-400">
            Choose Endpoint:
          </label>

          <div className="relative w-full sm:w-auto">
            <select
              id="apiSelect"
              className="appearance-none bg-[#1e293b] text-white font-mono border border-white/10 px-4 py-3 pr-10 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={selectedEndpoint}
              onChange={(e) => setSelectedEndpoint(e.target.value)}
            >
              {
                Object.entries(endpoints).map(([key, ep]) => (
                  <option key={key} value={key}>
                    {ep.method} {ep.path}
                  </option>
                ))}
            </select>

            {/* Custom dropdown arrow (unchanged Tailwind + SVG) */}
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>


      {/* Dynamic Endpoint Label */}
      <div className="flex items-center justify-center max-w-3xl gap-2 px-4 py-2 mx-auto mb-6 text-sm text-white rounded-full bg-white/10">
        {getEndpointLabel(selectedEndpoint)}
      </div>

      <div className="m-4 text-center">
        <a
          href="https://documenter.getpostman.com/view/7208866/2sB34bKiYR"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-orange-500 rounded-full hover:bg-orange-400"
        >
          <img src={postmanIcon} alt="Postman Icon" className="w-5 h-5" />
          View Full Postman Collection
        </a>
      </div>
      {/* JSON Input / Response UI */}
      <div className="grid gap-8 mx-auto max-w-7xl md:grid-cols-2">
        {/* Left: JSON Request */}
        <div>
          <label htmlFor="jsonInput" className="block mb-2 text-sm text-gray-400">
            Request JSON
          </label>
          <textarea
            id="jsonInput"
            rows="16"
            className="w-full p-4 text-sm font-mono rounded-lg bg-[#1e293b] text-white border border-white/10 resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
          />
          <button
            id="sendBtn"
            disabled={isRequestPending}
            onClick={handleSendRequest}
            className={`px-6 py-2 mt-4 text-sm font-semibold transition rounded-full ${isRequestPending ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-500'
              }`}
          >
            {isRequestPending ? 'Sending...' : 'Send Request'}
          </button>
        </div>

        {/* Right: JSON Response */}
        <div className="w-full p-4 rounded-lg bg-[#1e293b] text-green-400 border border-white/10 min-h-[360px]">
          {jobStatus === 'completed' && jobResult && (
            <>
              <pre className="font-mono text-sm text-green-400 whitespace-pre-wrap">
                {JSON.stringify(jobResult, null, 2)}
              </pre>
              {/** Download button */}
              {jobResult?.result_url && (
                <div className="mb- 4">
                  <a
                    href={jobResult.result_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-4 py-2 text-sm font-semibold text-white rounded bg-violet-600 hover:bg-violet-500"
                  >
                    📄 Download Result
                  </a>
                </div>
              )}

            </>
          )}
          {jobStatus === 'failed' && (
            <p className="font-mono text-red-400">❌ {pollingError}</p>
          )}

          {jobStatus && jobStatus != "completed" && (
            <p className={`font-mono text-sm mb-4 ${jobStatus === 'completed' ? 'text-green-400' :
              jobStatus === 'failed' ? 'text-red-400' :
                'text-yellow-400'
              }`}>
              ⏳ Job Status: {jobStatus}
            </p>
          )}

        </div>

      </div>
      {/* Postman Collection CTA */}
    </section>
  );
}